import { api } from '../client';

export interface WorkOrder {
  id: string;
  companyId: string;
  workOrderNumber: string;
  type: string;
  priority: string;
  status: string;
  title: string;
  description?: string;
  equipmentId?: string;
  equipment?: {
    id: string;
    name: string;
    assetNumber: string;
    status?: string;
    condition?: string;
  };
  locationId: string;
  location?: {
    id: string;
    name: string;
    code: string;
  };
  assignedToId?: string;
  assignedTo?: {
    id: string;
    fullName: string;
    email?: string;
  };
  requestedById: string;
  requestedBy?: {
    id: string;
    fullName: string;
  };
  scheduledStart?: string;
  scheduledEnd?: string;
  actualStart?: string;
  actualEnd?: string;
  estimatedHours?: number;
  actualHours?: number;
  estimatedCost?: number;
  actualCost?: number;
  completionNotes?: string;
  lineItems?: WorkOrderLineItem[];
  statusHistory?: WorkOrderStatusHistory[];
  consumptions?: any[];
  createdAt: string;
  updatedAt: string;
  _count?: {
    lineItems: number;
  };
}

export interface WorkOrderLineItem {
  id: string;
  workOrderId: string;
  itemId: string;
  item?: {
    id: string;
    name: string;
    sku: string;
    unitCost?: number;
  };
  plannedQty: number;
  issuedQty: number;
  returnedQty: number;
  consumedQty: number;
  unit: string;
  unitCost?: number;
  notes?: string;
}

export interface WorkOrderStatusHistory {
  id: string;
  workOrderId: string;
  fromStatus?: string;
  toStatus: string;
  changedById: string;
  changedBy?: {
    id: string;
    fullName: string;
  };
  reason?: string;
  createdAt: string;
}

export interface QueryWorkOrderParams {
  type?: string;
  priority?: string;
  status?: string;
  equipmentId?: string;
  locationId?: string;
  assignedToId?: string;
  scheduledFrom?: string;
  scheduledTo?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface CreateWorkOrderDto {
  type: string;
  priority?: string;
  title: string;
  description?: string;
  equipmentId?: string;
  locationId: string;
  assignedToId?: string;
  scheduledStart?: string;
  scheduledEnd?: string;
  estimatedHours?: number;
  estimatedCost?: number;
  lineItems?: {
    itemId: string;
    plannedQty: number;
    unit: string;
    unitCost?: number;
    notes?: string;
  }[];
}

export interface UpdateWorkOrderDto extends Partial<CreateWorkOrderDto> {}

export interface WorkOrderUpdateStatusDto {
  status: string;
  reason?: string;
}

export interface CompleteWorkOrderDto {
  completionNotes?: string;
  actualHours?: number;
  actualCost?: number;
}

export interface WorkOrderDashboard {
  total: number;
  byStatus: Record<string, number>;
  byPriority: Record<string, number>;
  overdue: number;
  completedThisMonth: number;
  avgCompletionTime: number | null;
}

export const workOrdersApi = {
  getAll: (params?: QueryWorkOrderParams) =>
    api.get<{ data: WorkOrder[]; pagination: any }>('/api/work-orders', { params }),

  getById: (id: string) =>
    api.get<WorkOrder>(`/api/work-orders/${id}`),

  getDashboard: () =>
    api.get<WorkOrderDashboard>('/api/work-orders/dashboard'),

  create: (data: CreateWorkOrderDto) =>
    api.post<WorkOrder>('/api/work-orders', data),

  update: (id: string, data: UpdateWorkOrderDto) =>
    api.patch<WorkOrder>(`/api/work-orders/${id}`, data),

  updateStatus: (id: string, data: WorkOrderUpdateStatusDto) =>
    api.patch<WorkOrder>(`/api/work-orders/${id}/status`, data),

  complete: (id: string, data: CompleteWorkOrderDto) =>
    api.post<WorkOrder>(`/api/work-orders/${id}/complete`, data),

  delete: (id: string) =>
    api.delete(`/api/work-orders/${id}`),

  // Line Items
  addLineItem: (workOrderId: string, data: { itemId: string; plannedQty: number; unit: string; unitCost?: number; notes?: string }) =>
    api.post<WorkOrderLineItem>(`/api/work-orders/${workOrderId}/items`, data),

  removeLineItem: (workOrderId: string, itemId: string) =>
    api.delete(`/api/work-orders/${workOrderId}/items/${itemId}`),

  issueParts: (workOrderId: string, itemId: string, quantity: number) =>
    api.post<WorkOrderLineItem>(`/api/work-orders/${workOrderId}/items/${itemId}/issue`, { quantity }),

  returnParts: (workOrderId: string, itemId: string, quantity: number, notes?: string) =>
    api.post<WorkOrderLineItem>(`/api/work-orders/${workOrderId}/items/${itemId}/return`, { quantity, notes }),
};

export const WORK_ORDER_TYPES = [
  { value: 'PREVENTIVE', label: 'Preventive' },
  { value: 'CORRECTIVE', label: 'Corrective' },
  { value: 'EMERGENCY', label: 'Emergency' },
  { value: 'INSPECTION', label: 'Inspection' },
  { value: 'INSTALLATION', label: 'Installation' },
  { value: 'DECOMMISSION', label: 'Decommission' },
];

export const WORK_ORDER_PRIORITIES = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'URGENT', label: 'Urgent' },
  { value: 'EMERGENCY', label: 'Emergency' },
];

export const WORK_ORDER_STATUSES = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'ON_HOLD', label: 'On Hold' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'CLOSED', label: 'Closed' },
];
